import { z } from 'zod';

// Error tracking without patient data (ADR 0007).

export const ERROR_SOURCES = ['server', 'client'] as const;
export type ErrorSource = (typeof ERROR_SOURCES)[number];

export const CLIENT_ERROR_KINDS = ['render', 'error', 'unhandledrejection'] as const;

/**
 * A route pattern such as /settings/users or /patients/$patientId, never the address
 * in the browser: that can carry IDs and search terms.
 */
const routePatternSchema = z
  .string()
  .max(200)
  .regex(/^\/[A-Za-z0-9/_$.-]*$/, 'Must be a route pattern');

/**
 * What the web app sends when something breaks in the browser. No URL, query string,
 * form values, page text or component state. The server redacts the message and stack
 * again, whatever the browser did.
 */
export const clientErrorReportSchema = z.object({
  kind: z.enum(CLIENT_ERROR_KINDS),
  name: z.string().max(100),
  message: z.string().max(2000),
  stack: z.string().max(8000).optional(),
  route: routePatternSchema,
});

export type ClientErrorReport = z.infer<typeof clientErrorReportSchema>;

export const errorGroupQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** The id of the last group on the previous page (its latest event). */
  cursor: z.uuid().optional(),
  source: z.enum(ERROR_SOURCES).optional(),
  /** Groups only the events of one request: the reference a person quotes. */
  requestId: z.string().max(64).optional(),
  /** Only events from the last this many days. */
  days: z.coerce.number().int().min(1).max(90).default(7),
});

/** Occurrences of one error, grouped by fingerprint. */
export const errorGroupSchema = z.object({
  fingerprint: z.string(),
  source: z.enum(ERROR_SOURCES),
  errorName: z.string(),
  errorCode: z.string().nullable(),
  route: z.string(),
  count: z.number().int(),
  users: z.number().int(),
  firstSeenAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  /** From the latest occurrence. */
  message: z.string(),
  stack: z.string().nullable(),
  release: z.string(),
  lastRequestId: z.string(),
  lastEventId: z.uuid(),
});

export type ErrorGroupView = z.infer<typeof errorGroupSchema>;

export const errorGroupPageSchema = z.object({
  items: z.array(errorGroupSchema),
  nextCursor: z.uuid().nullable(),
});
